import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Lock, Loader2, CheckCircle, AlertCircle, User, Shield } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const TechSetup = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
    name: ""
  });

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await axios.get(`${API}/api/auth/invite/${token}`);
      setTokenValid(true);
      setInviteInfo(res.data);
      setFormData(prev => ({ ...prev, name: res.data.name || "" }));
    } catch {
      setTokenValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/accept-invite`, {
        token,
        password: formData.password,
        name: formData.name
      });
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set up account");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FFCC00] animate-spin" />
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Invalid or Expired Link</h2>
          <p className="text-muted-foreground text-sm mb-6">
            This invite link is no longer valid. Please contact your employer to request a new invite.
          </p>
          <Button onClick={() => navigate("/tech/login")} className="bg-[#0046BE] text-white hover:bg-[#0046BE]/80">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2" data-testid="setup-success-title">Account Created!</h2>
          <p className="text-muted-foreground text-sm mb-6">
            Your technician account for <span className="text-[#FFCC00] font-semibold">{inviteInfo?.business_name}</span> is ready. You can now log in.
          </p>
          <Button
            onClick={() => navigate("/tech/login")}
            className="w-full bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90 font-semibold py-5"
            data-testid="go-to-login-btn"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-2xl bg-[#FFCC00] flex items-center justify-center shadow-lg">
              <span className="text-3xl font-black text-black">F</span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']">Fieldz AI</h1>
          <p className="text-muted-foreground text-sm mt-2">
            Set up your technician account for <span className="text-[#FFCC00] font-semibold">{inviteInfo?.business_name}</span>
          </p>
        </div>

        {/* Form Card */}
        <div className="rounded-2xl border border-border bg-card shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-[#FFCC00] via-[#0046BE] to-[#FFCC00]" />
          <div className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-1">Create Your Account</h2>
            <p className="text-muted-foreground text-sm mb-6">Set a password to access your technician portal</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-foreground text-sm">Your Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-background border-border text-foreground pl-10"
                    placeholder="Your full name"
                    data-testid="tech-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground text-sm">Email</Label>
                <Input
                  value={inviteInfo?.email || ""}
                  className="bg-muted border-border text-muted-foreground"
                  disabled
                />
              </div>

              <div className="space-y-2">
                <Label className="text-foreground text-sm">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-background border-border text-foreground pl-10"
                    placeholder="Create a password (min. 6 characters)"
                    required
                    data-testid="tech-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground text-sm">Confirm Password</Label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="bg-background border-border text-foreground pl-10"
                    placeholder="Confirm your password"
                    required
                    data-testid="tech-confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90 font-semibold py-5 mt-2"
                disabled={submitting}
                data-testid="tech-setup-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting up...</>
                ) : (
                  <><CheckCircle className="w-4 h-4 mr-2" /> Complete Setup</>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechSetup;
